// Sonic Coop: the browser app (home, lobby, game).

import { Client, type Room } from "colyseus.js";
import { CHARACTERS, GAME_SETTINGS, isCharacterAvailable, type GameSettings, type LobbyState, type StartInfo, type GameId } from "@sonic-coop/engine";
import { GameRunner } from "./game/runner.js";
import { Sonic3Runner } from "./game/sonic3-runner.js";
import { Input } from "./input.js";
import { Chat, ChatPanel } from "./chat.js";
import "@fontsource/press-start-2p/latin-400.css";
import "./style.css";

const API = `${location.origin}/api`;
const client = new Client(API);
const input = new Input();
const app = document.getElementById("app")!;

let room: Room | null = null;
let lobby: LobbyState | null = null;
let runner: GameRunner | Sonic3Runner | null = null;
let chat: Chat | null = null;
let chatPanel: ChatPanel | null = null;
let reconnectToken = "";
let leaving = false;

const GAME_NAMES: Record<GameId, string> = { sonic1: "Sonic the Hedgehog", sonic2: "Sonic the Hedgehog 2", sonic3: "Sonic the Hedgehog 3 (experimental)", soniccd: "Sonic the Hedgehog CD" };
const GAME_SHORT: Record<GameId, string> = { sonic1: "Sonic 1", sonic2: "Sonic 2", sonic3: "Sonic 3", soniccd: "Sonic CD" };

function el<K extends keyof HTMLElementTagNameMap>(tag: K, attrs: Record<string, string> = {}, ...children: (Node | string)[]): HTMLElementTagNameMap[K] {
  const e = document.createElement(tag);
  for (const [k, v] of Object.entries(attrs)) {
    if (k === "class") e.className = v;
    else e.setAttribute(k, v);
  }
  e.append(...children);
  return e;
}

/** Two linked rings: the top crossing is the right ring's, the bottom one the left ring's (redrawn over it). */
const RINGS_SVG = `<svg class="rings" viewBox="0 0 120 80" aria-hidden="true"><defs>
<linearGradient id="ring-gold" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stop-color="#fff6a8"/><stop offset=".45" stop-color="#ffd21f"/><stop offset="1" stop-color="#e07b00"/></linearGradient>
<clipPath id="ring-cross"><rect x="48" y="44" width="24" height="36"/></clipPath></defs><g fill="none">
<g id="ring-left"><circle cx="42" cy="40" r="28" stroke="#000" stroke-width="15"/><circle cx="42" cy="40" r="28" stroke="url(#ring-gold)" stroke-width="8"/></g>
<circle cx="78" cy="40" r="28" stroke="#000" stroke-width="15"/><circle cx="78" cy="40" r="28" stroke="url(#ring-gold)" stroke-width="8"/>
<use href="#ring-left" clip-path="url(#ring-cross)"/>
<path d="M22 30a22 22 0 0 1 12-12M58 30a22 22 0 0 1 12-12" stroke="#fffbe0" stroke-width="3" stroke-linecap="square"/></g></svg>`;

function logo(small: boolean): HTMLHeadingElement {
  const rings = el("span", { class: "rings-wrap" });
  rings.innerHTML = RINGS_SVG;
  return el("h1", { class: small ? "logo small" : "logo", "aria-label": "Sonic Coop" }, rings,
    el("span", { class: "wordmark" }, el("span", { class: "w-sonic" }, "Sonic"), el("span", { class: "w-coop" }, "Co-op")));
}

// ---- artwork rendered from the user's own game data at extraction time (tools/extract-ui.ts) ---------------------

interface UiArt {
  cover?: string;
  demo?: string;
  chars: Record<string, string>;
  stages: Record<string, string>;
}

const art = new Map<GameId, UiArt | null>();
const artLoading = new Set<GameId>();

/** The game's artwork if it has loaded (null when there is none); starts loading it otherwise. */
function artFor(game: GameId): UiArt | null {
  if (game === "sonic3") return null; // the experimental Sonic 3 mode has no artwork export
  if (art.has(game)) return art.get(game)!;
  if (!artLoading.has(game)) {
    artLoading.add(game);
    fetch(`/data/ui/${game}/ui.json`)
      .then((r) => (r.ok ? (r.json() as Promise<UiArt>) : null))
      .catch(() => null)
      .then((a) => {
        art.set(game, a);
        // preload the hover images so they swap in instantly
        if (a?.demo) new Image().src = artUrl(game, a.demo);
        if (lobby && !runner) renderLobby();
        else if (!room) refreshHomeCovers();
      });
  }
  return null;
}

function artUrl(game: GameId, file: string): string {
  return `/data/ui/${game}/${file}`;
}

function pixelImg(src: string, alt: string, cls = ""): HTMLImageElement {
  const img = el("img", { src, alt, class: `pixel ${cls}`.trim(), decoding: "async" });
  img.onerror = () => img.remove();
  return img;
}

/** A game's card: its title screen (gameplay on hover), or a text card when there is no artwork. */
function gameCard(game: GameId, selected: boolean): HTMLButtonElement {
  const a = artFor(game);
  const media = el("span", { class: "cover" });
  if (a?.cover) media.append(pixelImg(artUrl(game, a.cover), `${GAME_NAMES[game]} title screen`, "front"));
  if (a?.demo) media.append(pixelImg(artUrl(game, a.demo), "", "back"));
  if (!a?.cover) media.append(el("span", { class: "cover-text" }, GAME_SHORT[game]));
  return el("button", { class: `game-card game-${game}${selected ? " selected" : ""}`, title: GAME_NAMES[game] },
    media, el("span", { class: "caption" }, el("strong", {}, GAME_SHORT[game]), el("small", {}, GAME_NAMES[game])));
}

let homeCovers: HTMLElement | null = null;
let installedGames: GameId[] = [];

function refreshHomeCovers(): void {
  if (!homeCovers) return;
  const games = installedGames.filter((g) => artFor(g)?.cover);
  homeCovers.replaceChildren(...games.map((g) => el("figure", { class: "home-cover" }, pixelImg(artUrl(g, artFor(g)!.cover!), GAME_NAMES[g]))));
  homeCovers.hidden = games.length === 0;
}

function toast(msg: string, kind: "info" | "error" = "info"): void {
  const t = el("div", { class: `toast ${kind}` }, msg);
  document.body.append(t);
  setTimeout(() => t.classList.add("show"), 10);
  setTimeout(() => {
    t.classList.remove("show");
    setTimeout(() => t.remove(), 400);
  }, 3500);
}

function playerName(): string {
  try {
    return localStorage.getItem("ringlink.name") ?? "";
  } catch {
    return "";
  }
}

function saveName(name: string): void {
  try {
    localStorage.setItem("ringlink.name", name);
  } catch {
    /* ignore */
  }
}

function errorText(e: unknown): string {
  const m = e instanceof Error ? e.message : String(e);
  if (/not found|no room|invalid|ERR_MATCHMAKE_INVALID_ROOM_ID/i.test(m)) return "There is no room with that code.";
  if (/full|locked/i.test(m)) return "That room is full.";
  if (/in progress/i.test(m)) return "That room's game has already started.";
  if (/Failed to fetch|NetworkError|ECONNREFUSED/i.test(m)) return "Could not reach the game server.";
  return m;
}

// ---- home ----------------------------------------------------------------------------------------------------------

function showHome(message?: string): void {
  runner?.stop();
  runner = null;
  room = null;
  lobby = null;
  chatPanel?.dispose();
  chatPanel = chat = null;
  const invite = new URLSearchParams(location.search).get("room") ?? "";
  history.replaceState(null, "", location.pathname + (invite && !message ? `?room=${invite}` : ""));
  const nameInput = el("input", { id: "name", maxlength: "16", placeholder: "Your name", autocomplete: "nickname" });
  nameInput.value = playerName();
  const codeInput = el("input", { id: "code", placeholder: "CODE", autocomplete: "off", spellcheck: "false" });
  codeInput.value = invite.toUpperCase();
  // Accepts a bare code or a pasted invite link
  const cleanCode = (text: string) => {
    const fromLink = /[?&]room=([A-Za-z]+)/.exec(text);
    return (fromLink ? fromLink[1] : text).toUpperCase().replace(/[^A-Z]/g, "").slice(0, 4);
  };
  codeInput.addEventListener("input", () => (codeInput.value = cleanCode(codeInput.value)));
  const createBtn = el("button", { class: "primary" }, "Create a lobby");
  const joinBtn = el("button", {}, "Join");
  const status = el("p", { class: "status" }, message ?? "");

  const busy = (b: boolean) => {
    createBtn.disabled = joinBtn.disabled = b;
  };
  createBtn.onclick = async () => {
    busy(true);
    status.textContent = "Creating a lobby…";
    saveName(nameInput.value.trim());
    try {
      enterRoom(await client.create("coop", { name: nameInput.value.trim() }));
    } catch (e) {
      status.textContent = errorText(e);
      busy(false);
    }
  };
  const join = async () => {
    const code = cleanCode(codeInput.value);
    if (code.length !== 4) {
      status.textContent = "Room codes have four letters.";
      return;
    }
    busy(true);
    status.textContent = "Joining…";
    saveName(nameInput.value.trim());
    try {
      const info = await fetch(`${API}/rooms/${code}`);
      if (info.status === 404) throw new Error("not found");
      const r = info.ok ? ((await info.json()) as { clients: number; maxClients: number }) : null;
      if (r && r.clients >= r.maxClients) throw new Error("full");
      enterRoom(await client.joinById(code, { name: nameInput.value.trim() }));
    } catch (e) {
      status.textContent = errorText(e);
      busy(false);
    }
  };
  joinBtn.onclick = join;
  codeInput.addEventListener("keydown", (e) => e.key === "Enter" && join());

  app.replaceChildren(
    el(
      "main",
      { class: "home" },
      el("header", { class: "hero" }, logo(false), el("p", { class: "tagline" }, "Play classic Sonic with a friend, online, right in your browser."),
        el("p", { class: "games-line" }, "Sonic 1 \u00b7 Sonic 2 \u00b7 Sonic CD")),
      (homeCovers = el("div", { class: "home-covers", hidden: "" })),
      el("label", { class: "field" }, el("span", {}, "Name"), nameInput),
      el("div", { class: "card" }, el("h2", {}, "Host"), el("p", {}, "Start a lobby and send the invite link to a friend."), createBtn),
      el("div", { class: "card" }, el("h2", {}, "Join"), el("div", { class: "row" }, codeInput, joinBtn)),
      status,
      el(
        "section",
        { class: "card about" },
        el("h2", {}, "What is this?"),
        el("p", {}, "The original Sonic games, rebuilt for online co-op. Each player runs with their own screen, character, rings and HUD, while the levels, badniks and bosses are shared, so you can race ahead or team up on Eggman."),
        el(
          "ol",
          { class: "steps" },
          el("li", {}, el("strong", {}, "Host"), "Pick a game, a character and a level."),
          el("li", {}, el("strong", {}, "Invite"), "Send your friend the lobby link or room code."),
          el("li", {}, el("strong", {}, "Play"), "Fall behind? Hit Regroup to warp to your partner."),
        ),
        el("p", { class: "chips" }, ...["No install", "Keyboard", "Gamepad", "Touch", "Infinite lives"].map((c) => el("span", {}, c))),
      ),
      el("footer", {}, "Uses your own Sonic game data. Fan project, not affiliated with SEGA."),
    ),
  );
  (invite ? codeInput : nameInput).focus();
  fetch(`${API}/health`)
    .then((r) => r.json() as Promise<{ games?: GameId[] }>)
    .then((h) => {
      installedGames = (h.games ?? []).filter((g) => g !== "sonic3");
      for (const g of installedGames) artFor(g);
      refreshHomeCovers();
    })
    .catch(() => {});
}

// ---- lobby -------------------------------------------------------------------------------------------------------

function enterRoom(r: Room): void {
  room = r;
  reconnectToken = r.reconnectionToken;
  leaving = false;
  chatPanel?.dispose();
  chat = new Chat(r);
  chatPanel = new ChatPanel(chat);
  r.onMessage("lobby", (l: LobbyState) => {
    lobby = l;
    history.replaceState(null, "", `?room=${l.code}`);
    if (!runner) renderLobby();
    else if (runner instanceof GameRunner) runner.onLobby();
  });
  r.onMessage("start", (info: StartInfo) => startGame(info));
  r.onMessage("notice", (m: string) => toast(m));
  r.onMessage("end", (m: { message: string }) => {
    runner?.stop();
    runner = null;
    toast(m.message);
    renderLobby();
  });
  r.onMessage("event", () => {});
  r.onMessage("snap", () => {});
  r.onMessage("world", () => {});
  r.onMessage("pong", () => {});
  r.onError((code, message) => toast(`Server error ${code}: ${message}`, "error"));
  r.onLeave((code) => {
    if (leaving) return;
    if (code === 1000) {
      showHome("You left the room.");
      return;
    }
    void tryReconnect();
  });
  app.replaceChildren(el("main", { class: "lobby" }, el("p", { class: "status" }, "Connecting…")));
}

async function tryReconnect(): Promise<void> {
  toast("Connection lost, reconnecting…", "error");
  const deadline = Date.now() + 55_000;
  while (Date.now() < deadline) {
    try {
      const r = await client.reconnect(reconnectToken);
      runner?.stop();
      runner = null;
      enterRoom(r);
      toast("Reconnected");
      return;
    } catch {
      await new Promise((res) => setTimeout(res, 2000));
    }
  }
  showHome("The connection to the room was lost.");
}

const SETTING_LABELS: Record<keyof GameSettings, { label: string; hint: string }> = {
  infiniteLives: { label: "Infinite lives", hint: "Lives never run out" },
  respawn: { label: "Respawn", hint: "Where a player who dies comes back" },
  spindash: { label: "Spin dash", hint: "Hold down and press jump to charge" },
  tailsFlight: { label: "Tails can fly", hint: "Press jump in the air as Tails" },
  knucklesClimb: { label: "Knuckles can climb", hint: "Otherwise he drops off walls he glides into" },
  peelOut: { label: "Super peel out", hint: "Sonic: hold up and press jump to charge" },
};

/** A checkbox option of the lobby's Options section. */
function toggleOption(label: string, hint: string, checked: boolean, isHost: boolean, onChange: (checked: boolean) => void): HTMLElement {
  const box = el("input", { type: "checkbox" });
  box.checked = checked;
  box.disabled = !isHost;
  box.onchange = () => onChange(box.checked);
  return el("label", { class: "option toggle", title: hint }, box, el("span", {}, label), el("small", {}, hint));
}

/** The host's options for the selected game, and the room's chat; read-only for everyone else. */
function settingsForm(l: LobbyState, offered: (keyof GameSettings)[], isHost: boolean): HTMLElement {
  const settings = l.settings;
  const form = el("div", { class: "options" });
  for (const key of offered) {
    const { label, hint } = SETTING_LABELS[key];
    if (key === "respawn") {
      const select = el("select", {}, el("option", { value: "checkpoint" }, "Last checkpoint"), el("option", { value: "partner" }, "Next to partner"));
      select.value = settings.respawn;
      select.onchange = () => room!.send("lobby", { setSettings: { respawn: select.value as GameSettings["respawn"] } });
      select.disabled = !isHost;
      form.append(el("label", { class: "option select", title: hint }, select, el("span", {}, label), el("small", {}, hint)));
    } else {
      form.append(toggleOption(label, hint, settings[key], isHost, (checked) => room!.send("lobby", { setSettings: { [key]: checked } })));
    }
  }
  form.append(toggleOption("Chat", "Players can send each other messages (Y in game)", l.chat, isHost, (checked) => room!.send("lobby", { setChat: checked })));
  return form;
}

function renderLobby(): void {
  if (!room || !lobby) return;
  const l = lobby;
  const me = l.players.find((p) => p.id === room!.sessionId);
  const isHost = l.hostId === room.sessionId;
  const invite = `${location.origin}${location.pathname}?room=${l.code}`;

  const copyBtn = el("button", {}, "Copy invite link");
  copyBtn.onclick = async () => {
    try {
      await navigator.clipboard.writeText(invite);
      toast("Invite link copied");
    } catch {
      prompt("Copy this link:", invite);
    }
  };

  const games = el("div", { class: "choices" });
  const listed: GameId[] = ["sonic1", "sonic2"];
  if (l.games?.includes("soniccd") || l.game === "soniccd") listed.push("soniccd");
  if (l.games?.includes("sonic3") || l.game === "sonic3") listed.push("sonic3");
  games.className = "game-cards";
  for (const g of listed) {
    const b = gameCard(g, l.game === g);
    b.disabled = (!isHost && l.game !== g) || l.phase !== "lobby";
    if (!isHost) b.classList.add("readonly");
    b.onclick = () => room!.send("lobby", { setGame: g });
    games.append(b);
  }

  const chars = el("div", { class: "char-cards" });
  const gameArt = artFor(l.game);
  for (const c of CHARACTERS.filter((character) => isCharacterAvailable(l.game, character.id))) {
    const portrait = gameArt?.chars[c.id];
    const b = el("button", { class: `char-card char-${c.name.toLowerCase()}${me?.character === c.id ? " selected" : ""}` },
      portrait ? pixelImg(artUrl(l.game, portrait), c.name, "portrait") : el("span", { class: "portrait-text" }, c.name[0]),
      el("strong", {}, c.name));
    b.disabled = l.game === "sonic3";
    b.onclick = () => room!.send("lobby", { setCharacter: c.id });
    chars.append(b);
  }

  const list = el("ul", { class: "players" });
  for (let s = 0; s < l.maxPlayers; s++) {
    const p = l.players.find((x) => x.slot === s);
    list.append(
      p
        ? el(
            "li",
            { class: p.ready ? "ready" : "" },
            el("span", { class: "slot" }, `P${s + 1}`),
            el("span", { class: "name" }, p.name + (p.id === l.hostId ? " ★" : "") + (p.id === room.sessionId ? " (you)" : "")),
            el("span", { class: "char" }, CHARACTERS.find((character) => character.id === p.character)?.name ?? "?"),
            el("span", { class: "state" }, !p.connected ? "reconnecting…" : p.ready ? "Ready" : "Not ready"),
          )
        : el("li", { class: "empty" }, el("span", { class: "slot" }, `P${s + 1}`), el("span", { class: "name" }, "Waiting for a player…")),
    );
  }

  const readyBtn = el("button", { class: me?.ready ? "" : "primary" }, me?.ready ? "Not ready" : "Ready");
  readyBtn.onclick = () => room!.send("lobby", { ready: !me?.ready });
  const allReady = l.players.length > 0 && l.players.every((p) => p.ready);
  const startBtn = el("button", { class: "primary" }, l.players.length < 2 ? "Start (solo)" : "Start");
  startBtn.disabled = !isHost || !allReady;
  startBtn.onclick = () => room!.send("lobby", { start: true });
  const leaveBtn = el("button", { class: "danger" }, "Leave");
  leaveBtn.onclick = () => {
    leaving = true;
    void room?.leave(true);
    showHome();
  };

  const stages = l.stages ?? [];
  const current = stages[Math.min(l.startStage, stages.length - 1)];
  const stageLabel = (s: { level: string; act: string }) => `${s.level} - ${s.act}`;
  const stageSection: HTMLElement[] = [];
  if (stages.length) {
    const stage = el("select", {}, ...stages.map((s, i) => el("option", { value: String(i) }, stageLabel(s))));
    stage.value = String(Math.max(0, stages.indexOf(current)));
    stage.onchange = () => room!.send("lobby", { setStartStage: Number(stage.value) });
    stage.disabled = !isHost;
    const thumb = current && gameArt?.stages[String(current.listPos ?? stages.indexOf(current))];
    const preview = thumb
      ? el("figure", { class: "stage-preview" }, pixelImg(artUrl(l.game, thumb), stageLabel(current)), el("figcaption", {}, current.level, el("small", {}, current.act)))
      : "";
    stageSection.push(el("section", {}, el("h2", {}, "Level"), el("div", { class: "stage-pick" }, preview, isHost || !current ? stage : el("p", {}, stageLabel(current)))));
  }
  const extras: HTMLElement[] = [];
  const offered = GAME_SETTINGS[l.game] ?? [];
  if (l.settings) extras.push(el("section", {}, el("h2", {}, "Options"), settingsForm(l, offered, isHost)));
  // chat once there is someone to talk to
  const chatFocused = chatPanel?.focused() ?? false;
  if (chatPanel && l.chat && l.players.length > 1) {
    chatPanel.mySlot = me?.slot ?? -1;
    chatPanel.redraw();
    extras.push(chatPanel.root);
  }

  app.replaceChildren(
    el(
      "main",
      { class: "lobby" },
      el("header", {}, logo(true), el("div", { class: "code" }, el("small", {}, "Room code"), el("strong", {}, l.code)), copyBtn),
      l.message ? el("p", { class: "notice" }, l.message) : "",
      el("section", {}, el("h2", {}, "Game"), games),
      ...stageSection,
      el("section", {}, el("h2", {}, "Your character"), chars, l.game === "sonic3"
        ? el("p", { class: "hint" }, "Sonic 3 is experimental: player 1 is Sonic and player 2 is Tails.")
        : l.game === "soniccd"
          ? el("p", { class: "hint" }, "Sonic CD supports co-op: each player controls their own character.")
          : ""),
      el("section", {}, el("h2", {}, "Players"), list),
      ...extras,
      el("div", { class: "row actions" }, readyBtn, startBtn, leaveBtn),
      el("p", { class: "hint" }, isHost ? (allReady ? "Everyone is ready." : "Start when everyone is ready.") : "The host starts the game when everyone is ready."),
      el("details", { class: "controls" }, el("summary", {}, "Controls"), el("p", {}, "Move: arrow keys or WASD · Jump: Z / X / C / Space · Regroup (warp the player furthest behind to the leader): T · Chat: Y · Fullscreen: F11 · Gamepads work too (A/B/X jump, Y regroup).")),
    ),
  );
  if (chatFocused && chatPanel?.root.isConnected) chatPanel.restoreFocus();
}

// ---- game ----------------------------------------------------------------------------------------------------------

function startGame(info: StartInfo): void {
  if (!room) return;
  runner?.stop();
  const root = el("main", { class: "game" });
  app.replaceChildren(root);
  const ui = {
    root,
    toast,
    chat: chat ?? undefined,
    onExit: () => {
      leaving = true;
      runner?.stop();
      runner = null;
      void room?.leave(true);
      showHome();
    },
  };
  runner = info.game === "sonic3" ? new Sonic3Runner(room, info, () => lobby!, input, ui) : new GameRunner(room, info, () => lobby!, input, ui);
  runner.start().catch((e) => {
    toast(errorText(e), "error");
    runner?.stop();
    runner = null;
    renderLobby();
  });
}

showHome();
